import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface RenameThreadDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentName: string;
    onConfirm: (newName: string) => Promise<void> | void;
}

export function RenameThreadDialog({
    open,
    onOpenChange,
    currentName,
    onConfirm
}: RenameThreadDialogProps) {
    const [name, setName] = useState(currentName);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setName(currentName);
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 50);
        }
    }, [open, currentName]);

    const handleConfirm = async () => {
        const trimmed = name.trim();
        if (!trimmed || trimmed === currentName) {
            onOpenChange(false);
            return;
        }

        setIsSaving(true);
        try {
            await onConfirm(trimmed);
            onOpenChange(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="gap-4 p-6 sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle className="font-semibold">
                        Rename chat
                    </DialogTitle>
                </DialogHeader>
                <Input
                    ref={inputRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSaving}
                    className="h-10"
                />
                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        variant="ghost"
                        size="medium"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="medium"
                        onClick={handleConfirm}
                        disabled={isSaving || !name.trim()}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
